import ThermalPrinterEncoder from "../thermal-printer-encoder"
import dayjs from "dayjs"

export interface PayInOutReceiptPayload {
  transaction: {
    id: string
    referenceNumber: number | string
    type: "IN" | "OUT"
    amount: number
    paymentMethod?: string
    notes?: string | null
    createdAt: string | Date
    category?: { name?: string } | null
    cashRegister?: { name?: string } | null
    createdBy?: { firstName?: string; lastName?: string; name?: string } | null
  }
  business: {
    name: string
    address?: string
    contactNumber?: string
    taxId?: string
    currencyCode?: string
  }
}

/**
 * Encode a pay in/out receipt for the thermal printer.
 *
 * @param payload - Transaction and business data used to build the receipt.
 * @returns Buffer containing ESC/POS commands ready to be printed.
 */
export async function encodePayInOutReceipt(
  payload: PayInOutReceiptPayload
): Promise<Uint8Array> {
  const { transaction, business } = payload
  const encoder = new ThermalPrinterEncoder({
    columns: 32,
    language: "esc-pos",
    feedBeforeCut: 1,
  })
  const dateTime = dayjs(transaction.createdAt).format("YYYY-MM-DD HH:mm:ss")
  const employeeName =
    transaction.createdBy?.name ||
    [transaction.createdBy?.firstName, transaction.createdBy?.lastName]
      .filter(Boolean)
      .join(" ")

  encoder.initialize()
  // Business header
  encoder.align("center")
  encoder.bold(true)
  encoder.text(business.name || "")
  encoder.bold(false)
  encoder.newline()
  if (business.address) {
    encoder.text(business.address)
    encoder.newline()
  }
  if (business.contactNumber) {
    encoder.text(`Tel: ${business.contactNumber}`)
    encoder.newline()
  }
  if (business.taxId) {
    encoder.text(`Tax ID: ${business.taxId}`)
    encoder.newline()
  }
  encoder.newline()
  // Receipt title
  encoder.bold(true)
  encoder.text(transaction.type === "IN" ? "PAY IN RECEIPT" : "PAY OUT RECEIPT")
  encoder.bold(false)
  encoder.newline()
  encoder.text(dateTime)
  encoder.newline()
  encoder.newline()
  // Transaction details
  encoder.align("left")
  encoder.text(`Reference No : ${transaction.referenceNumber}`)
  encoder.newline()
  if (transaction.cashRegister?.name) {
    encoder.text(`Cash Register: ${transaction.cashRegister.name}`)
    encoder.newline()
  }
  if (transaction.category?.name) {
    encoder.text(`Category     : ${transaction.category.name}`)
    encoder.newline()
  }
  if (employeeName) {
    encoder.text(`Employee     : ${employeeName}`)
    encoder.newline()
  }
  if (transaction.paymentMethod) {
    encoder.text(`Payment Mode : ${transaction.paymentMethod}`)
    encoder.newline()
  }
  if (transaction.notes) {
    encoder.text(`Notes        : ${transaction.notes}`)
    encoder.newline()
  }
  encoder.newline()
  encoder.bold(true)
  const label = transaction.type === "IN" ? "Amount In" : "Amount Out"
  const currency = business.currencyCode ? `${business.currencyCode} ` : ""
  encoder.text(
    `${label}   : ${currency}${Number(transaction.amount).toFixed(2)}`
  )
  encoder.bold(false)
  encoder.newline()
  encoder.newline()
  encoder.align("center")
  encoder.text("Thank you!")
  encoder.newline()
  encoder.newline()
  // Cut paper
  encoder.cut()
  return encoder.encode()
}
